/**
 * obliqueBindings.js — три ползунка наклона наконец поворачивают камеру.
 *
 * ПОВОД. Ползунки «Угол X / Y / Z» существовали в разметке, а их единственный
 * обработчик менял ТЕКСТОВУЮ ПОДПИСЬ рядом с ползунком. Камера не двигалась ни
 * на градус; кнопка сброса обнуляла ползунки и печатала «Oblique reset». То
 * есть косых реформатов в программе не было — было обещание тремя ползунками.
 *
 * Математика поворота — в `core/obliqueRotation.js`, без импортов и под
 * аналитическим тестом (26 проверок: 0° ничего не меняет, 90° вокруг известной
 * оси даёт известный ответ, 360° возвращает исходное, нормаль и верх остаются
 * единичными и перпендикулярными, порядок X→Y→Z закреплён числом). Здесь —
 * только связь с окном: какое окно наклоняем, откуда берём исходную ориентацию
 * и как возвращаем назад.
 *
 * ТРИ РЕШЕНИЯ, КОТОРЫЕ ВАЖНО ЗНАТЬ.
 *
 * 1. НАКЛОНЯЕТСЯ АКТИВНОЕ ОКНО, а не все сразу. Наклонить все три реформата
 *    одними углами — бессмыслица: они смотрят в разные стороны, и «15° по X»
 *    для каждого означает своё. Врач наклоняет то окно, в которое смотрит.
 *
 * 2. ИСХОДНАЯ ОРИЕНТАЦИЯ ЗАПОМИНАЕТСЯ ОДИН РАЗ, при первом наклоне этого
 *    окна, и от неё считаются все последующие углы. Иначе наклоны копились бы:
 *    сдвинул ползунок на 10°, потом на 20° — и получил 30° вместо 20°.
 *
 * 3. СБРОС ВОЗВРАЩАЕТ ЗАПОМНЕННОЕ, а не «нулевые углы». Разница видна, когда
 *    исходная ориентация сама была не по осям (косая серия): нули дали бы не
 *    то, с чего начали.
 */

import { getRenderingEngine, текущееОкноСнимка } from '../core/viewportState.js';   // [12.0.57] выбранное окно, а не «активное или первое»
import { наклонить, естьНаклон, уголОтклонения } from '../core/obliqueRotation.js';
import { showToast } from '../utils/helpers.js';
import { log } from '../utils/logger.js';
import { on as busOn, EVENTS } from '../core/eventBus.js';   // [12.0.57] новый том / раскладка — забыть исходные ориентации

const ПОЛЗУНКИ = [['oblique-x', 'x'], ['oblique-y', 'y'], ['oblique-z', 'z']];

/** Исходная ориентация окна: id → {viewPlaneNormal, viewUp}. */
const _база = new Map();
/** [12.0.57] Углы каждого окна: id → {x, y, z} — ползунки показывают углы выбранного. */
const _углыОкон = new Map();
let _последнееОкно = null;

function $(id) { return document.getElementById(id); }

/**
 * Окно, которое наклоняем: ВЫБРАННОЕ щелчком окно реформата.
 *
 * [12.0.57] Прежде — «активное или первое» (getActiveOrFirstViewportId):
 * активное ядро не задаёт никто (12.0.49), и наклонялось окно под мышью или
 * первое в раскладке. На стенде (MPR 6): выбрано фронтальное окно, ползунок
 * «Угол X» на 30° — наклонилась СТОПКА «Нативные срезы» (первое окно). Стопку
 * наклонять вообще нельзя: её плоскость задана снимком, а не камерой, — после
 * такого «наклона» снимок смотрится сбоку. Теперь стопка и 3D — отказ с
 * причиной.
 * @returns {{id, vp}|{отказ:string}|null}
 */
function окно() {
  const re = getRenderingEngine();
  if (!re) return null;
  const id = текущееОкноСнимка();
  if (!id) return null;
  const vp = re.getViewport(id);
  if (!vp) return null;
  const тип = String(vp.type || '').toLowerCase();
  if (тип.includes('3d')) return { отказ: 'выбрано объёмное 3D-окно — его поворачивают мышью; щёлкните по окну реформата' };
  if (тип === 'stack') return { отказ: 'в стопке «Нативные срезы» плоскость задана самим снимком — наклон делается в окнах реформата (аксиальное, боковое, фронтальное): щёлкните по такому окну' };
  return { id, vp };
}

/** Прочитать углы с ползунков. */
function углы() {
  const о = { x: 0, y: 0, z: 0 };
  for (const [id, ключ] of ПОЛЗУНКИ) {
    const s = $(id);
    if (s) о[ключ] = Number(s.value) || 0;
  }
  return о;
}

/** Запомнить исходную ориентацию окна, если ещё не запомнили. */
function запомнитьБазу(id, vp) {
  if (_база.has(id)) return _база.get(id);
  const cam = vp.getCamera?.();
  if (!cam?.viewPlaneNormal || !cam?.viewUp) return null;
  const б = { viewPlaneNormal: [...cam.viewPlaneNormal], viewUp: [...cam.viewUp] };
  _база.set(id, б);
  return б;
}

/** Применить наклон к активному окну. */
export function применитьНаклон(двинутый = null) {
  const o = окно();
  if (!o || o.отказ) {
    // Молчать нельзя: ползунок двинулся, а на экране ничего — это ровно та
    // беда, из-за которой поломку не замечали.
    showToast(o?.отказ ? `Наклон: ${o.отказ}` : 'Наклон применять не к чему: откройте исследование и станьте в плоское окно', 'warning', 4000);
    return null;
  }
  // [12.0.57] Сменилось окно — ползунки, которые врач не трогал, встают на
  // углы ЭТОГО окна (или нули): иначе углы прошлого окна молча ложились бы на
  // новое вместе с тем, который двинули.
  if (_последнееОкно !== o.id) {
    const свои = _углыОкон.get(o.id) || { x: 0, y: 0, z: 0 };
    for (const [id, ключ] of ПОЛЗУНКИ) {
      if (id === двинутый) continue;
      const s = $(id); if (s) s.value = String(свои[ключ]);
      const v = $(`${id}-val`); if (v) v.textContent = `${свои[ключ]}°`;
    }
    _последнееОкно = o.id;
  }
  const б = запомнитьБазу(o.id, o.vp);
  if (!б) { showToast('Не удалось прочитать ориентацию окна', 'warning', 3000); return null; }

  const у = углы();
  _углыОкон.set(o.id, { ...у });
  const нов = наклонить(б, у);
  try {
    // setOrientation есть у объёмных окон Cornerstone; у стопки — только камера.
    if (typeof o.vp.setOrientation === 'function') {
      o.vp.setOrientation({ viewPlaneNormal: нов.viewPlaneNormal, viewUp: нов.viewUp }, false);
    } else {
      o.vp.setCamera({ viewPlaneNormal: нов.viewPlaneNormal, viewUp: нов.viewUp });
    }
    o.vp.render?.();
  } catch (e) {
    log.warn('[Наклон] окно не приняло ориентацию:', e.message);
    showToast('Это окно не поддерживает наклон (нужен объёмный реформат, не стопка срезов)', 'warning', 5000);
    return null;
  }

  const отклонение = уголОтклонения(б, у);
  подписать(отклонение, o.id);
  return { окно: o.id, ...нов, отклонение };
}

/** Сброс: вернуть запомненную ориентацию и обнулить ползунки. */
export function сброситьНаклон() {
  for (const [id] of ПОЛЗУНКИ) { const s = $(id); if (s) s.value = 0; }
  for (const [id, ключ] of ПОЛЗУНКИ) { const v = $(`${id}-val`); if (v) v.textContent = '0°'; void ключ; }

  const o = окно();
  if (!o || o.отказ) { подписать(0, null); return null; }
  _углыОкон.delete(o.id);
  const б = _база.get(o.id);
  if (!б) { подписать(0, o.id); return null; }
  try {
    if (typeof o.vp.setOrientation === 'function') o.vp.setOrientation(б, false);
    else o.vp.setCamera({ viewPlaneNormal: б.viewPlaneNormal, viewUp: б.viewUp });
    o.vp.render?.();
  } catch (e) { log.warn('[Наклон] сброс:', e.message); }
  подписать(0, o.id);
  showToast('Наклон снят — плоскость вернулась к исходной', 'info', 2000);
  return б;
}

/**
 * Подпись рядом с ползунками: суммарное отклонение плоскости.
 * Три отдельных угла врачу мало что говорят, а «плоскость наклонена на 23°» —
 * говорит.
 */
function подписать(отклонение, idОкна) {
  const el = $('oblique-total');
  if (!el) return;
  el.textContent = отклонение > 0.05
    ? `плоскость наклонена на ${отклонение.toFixed(1)}°${idОкна ? ` · ${idОкна}` : ''}`
    : 'плоскость по осям';
}

/** Подключить ползунки и кнопку сброса. Зовётся один раз при запуске. */
export function initOblique() {
  for (const [id] of ПОЛЗУНКИ) {
    const s = $(id);
    if (!s) continue;
    // input — на каждое движение (видно сразу), change — на отпускание.
    s.addEventListener('input', () => {
      const v = $(`${id}-val`);
      if (v) v.textContent = `${s.value}°`;
      применитьНаклон(id);
    });
  }
  const сброс = $('btn-oblique-reset');
  if (сброс) сброс.addEventListener('click', (e) => { e.stopPropagation(); сброситьНаклон(); });
  // [12.0.57] Исходная ориентация запоминалась навсегда: забытьБазу не звал
  // никто, и после открытия другого исследования «Сброс наклона» возвращал
  // окно к ориентации прошлого. Новый том или раскладка — окна новые.
  const забыть = () => {
    забытьБазу();
    for (const [id] of ПОЛЗУНКИ) { const s = $(id); if (s) s.value = 0; const v = $(`${id}-val`); if (v) v.textContent = '0°'; }
    подписать(0, null);
  };
  try { busOn(EVENTS.VOLUME_LOAD_DONE, забыть); busOn(EVENTS.LAYOUT_CHANGED, забыть); } catch (e) { log.debug('[Наклон] шина:', e.message); }
  log.debug('[Наклон] ползунки подключены');
}

/** Для проверок: что запомнено как исходное. */
export function базаОкна(id) { return _база.has(id) ? { ..._база.get(id) } : null; }

/** Забыть запомненное (смена исследования). */
export function забытьБазу() { _база.clear(); _углыОкон.clear(); _последнееОкно = null; }

export default { initOblique, применитьНаклон, сброситьНаклон, базаОкна, забытьБазу, естьНаклон };
